/**
 * Playing the synthesised sounds.
 *
 * core/audio produces samples and knows nothing about browsers; this turns
 * them into Web Audio buffers and plays them. The split is the same one that
 * lets the sounds be rendered to .wav headlessly - and it is what makes the
 * eventual port to a native build a matter of rewriting this file only.
 *
 * Two browser facts shape everything here:
 *
 *   - an AudioContext created before the user has interacted with the page
 *     starts suspended, and every sound played into it is silently dropped.
 *     Nothing throws. So the context is only created on the first gesture,
 *     which the click-to-play overlay already provides;
 *   - audio must never be able to break the game. A browser with sound
 *     blocked, an autoplay policy nobody predicted, a failed buffer - all of
 *     it has to end in silence, not in an exception on the frame loop.
 */

import { SYNTHS, type SoundName } from "../core/audio/synth.js";

export interface PlayOptions {
  /** 0..1, multiplied into the master level. */
  volume?: number;
  /** Playback rate, doubling as pitch. 1 is as synthesised. */
  rate?: number;
}

export class Audio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private readonly buffers = new Map<SoundName, AudioBuffer>();
  /** Decoded voice takes by url. `null` means it was tried and failed. */
  private readonly voices = new Map<string, AudioBuffer | null>();
  private readonly loading = new Set<string>();

  /** Every sound is generated once, on first use, and cached. */
  private bufferFor(name: SoundName): AudioBuffer | null {
    const ctx = this.ctx;
    if (ctx === null) return null;

    const cached = this.buffers.get(name);
    if (cached !== undefined) return cached;

    const sound = SYNTHS[name]();
    // The context's own rate may not be 44100 - it follows the output device,
    // and 48000 is common. Declaring the buffer at the rate it was synthesised
    // at makes the browser resample it, which is right; claiming the context's
    // rate would play it back at the wrong speed.
    const buffer = ctx.createBuffer(1, sound.samples.length, sound.sampleRate);
    // set() rather than copyToChannel(): copyToChannel's parameter type is
    // pinned to a plain ArrayBuffer, and core's Float32Array is not declared
    // that narrowly - deliberately, since core must not know what a browser
    // wants. Writing through the channel view sidesteps it and does the same.
    buffer.getChannelData(0).set(sound.samples);
    this.buffers.set(name, buffer);
    return buffer;
  }

  /**
   * Start the audio system. Safe to call repeatedly - the game calls it from
   * every click on the overlay, because a browser may refuse the first one.
   */
  unlock(): void {
    try {
      if (this.ctx === null) {
        this.ctx = new AudioContext();
        this.master = this.ctx.createGain();
        this.master.gain.value = 0.6;
        this.master.connect(this.ctx.destination);
      }
      // Suspended is the normal state before a gesture, and after a tab sleeps.
      if (this.ctx.state === "suspended") void this.ctx.resume();
    } catch (error) {
      // No audio is a degraded game; a crash here is no game at all.
      console.warn("audio unavailable", error);
      this.ctx = null;
    }
  }

  /**
   * Whether there is anything to play sound with yet.
   *
   * There is no AudioContext until somebody has clicked - browsers refuse to
   * make one before a gesture - so everything here quietly does nothing
   * until then. That is right for a footstep, which is gone in a moment, and
   * wrong for music: the director would hand a track over, hear no
   * complaint, and sit for two minutes believing it was playing.
   */
  get ready(): boolean {
    return this.ctx !== null && this.ctx.state === "running";
  }

  play(name: SoundName, options: PlayOptions = {}): void {
    const ctx = this.ctx;
    const master = this.master;
    if (ctx === null || master === null) return;

    const buffer = this.bufferFor(name);
    if (buffer === null) return;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    // A little pitch and level scatter on every hit. Identical repeats are the
    // giveaway that a sound is a recording being replayed - four blows on the
    // same tree should not be the same blow four times.
    source.playbackRate.value = (options.rate ?? 1) * (0.94 + Math.random() * 0.12);

    const gain = ctx.createGain();
    gain.gain.value = (options.volume ?? 1) * (0.85 + Math.random() * 0.3);

    source.connect(gain);
    gain.connect(master);
    source.start();
  }

  /**
   * Say a recorded line.
   *
   * Fetched and decoded once, then kept - a voice take is a few tens of
   * kilobytes and the same villager says the same thing repeatedly.
   *
   * **None of the scatter `play` applies.** A block sound repeated identically
   * gives itself away as a recording, so those get a little pitch and level
   * jitter; a human voice pitched up and down at random sounds like a fault in
   * the game. A person says a line the way they said it.
   *
   * Every failure here ends in silence: no network, a file that is not there,
   * a codec the browser will not take. The written line is on screen either
   * way, so silence costs the player nothing.
   */
  speak(url: string, options: PlayOptions = {}): void {
    const ctx = this.ctx;
    const master = this.master;
    if (ctx === null || master === null) return;

    const cached = this.voices.get(url);
    if (cached !== undefined) {
      this.playBuffer(cached, options.volume ?? 1);
      return;
    }
    // One request per url even if the line is triggered again while it loads.
    if (this.loading.has(url)) return;
    this.fetchVoice(url, options.volume ?? 1);
  }

  /**
   * Fetch and decode a take, and play it if somebody is waiting for it.
   *
   * @param playAt the level to play it at once decoded, or null to only load.
   */
  private fetchVoice(url: string, playAt: number | null): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    this.loading.add(url);
    void (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`${response.status} ${url}`);
        const buffer = await ctx.decodeAudioData(await response.arrayBuffer());
        this.voices.set(url, buffer);
        if (playAt !== null) this.playBuffer(buffer, playAt);
      } catch (error) {
        // Remembered as missing, so a broken take is not re-fetched on every
        // punch for the rest of the session.
        console.warn("voice unavailable", url, error);
        this.voices.set(url, null);
      } finally {
        this.loading.delete(url);
      }
    })();
  }

  /**
   * Fetch takes before anything asks for them.
   *
   * Without this the first punch is silent: `speak` starts the fetch and the
   * line arrives a moment later, which for a two-second shouted reply is the
   * difference between the joke landing and not. Called when the audio is
   * unlocked, which is the first click on the overlay - long before anybody
   * has walked up to a villager.
   *
   * Safe to call repeatedly: anything already loaded or in flight is skipped.
   */
  preload(urls: readonly string[]): void {
    for (const url of urls) {
      if (this.voices.has(url) || this.loading.has(url)) continue;
      this.fetchVoice(url, null);
    }
  }

  /* ---- music -------------------------------------------------------- */

  /*
   * Its own gain and its own source, kept apart from everything else.
   *
   * Music has a volume of its own because it is the one sound somebody turns
   * down without wanting the game to go quiet - an axe you cannot hear is a
   * missing answer, and a tune you cannot hear is a preference. It also has
   * to be stoppable in the middle, which no other sound here does.
   */
  private musicGain: GainNode | null = null;
  private musicNode: AudioBufferSourceNode | null = null;
  private readonly music = new Map<string, AudioBuffer | null>();
  private musicLevel = 0.5;
  private fetching: string | null = null;

  /** 0..1. Kept across tracks, and applied to the one playing now. */
  setMusicVolume(level: number): void {
    this.musicLevel = Math.max(0, Math.min(1, level));
    if (this.musicGain !== null) this.musicGain.gain.value = this.musicLevel;
  }

  get musicVolume(): number { return this.musicLevel; }

  /**
   * Start a track, fetching it if this is the first time.
   *
   * Nothing is downloaded until it is wanted: a dozen megabytes of music
   * fetched at the door would be a game that takes a minute to open on a
   * connection that has to carry it. The call returns at once either way -
   * if the file is not here yet it arrives and starts a moment later, and a
   * track that was asked for and then countermanded simply never starts.
   */
  playMusic(url: string): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    this.stopMusic();
    const have = this.music.get(url);
    if (have === null) return;             // tried once, and it is not there
    if (have !== undefined) { this.startMusic(have); return; }
    if (this.fetching === url) return;
    this.fetching = url;
    void (async () => {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(String(response.status));
        const buffer = await ctx.decodeAudioData(await response.arrayBuffer());
        this.music.set(url, buffer);
        // Only if nothing has been asked for since: the player may have left
        // the кремль, or the map, while this was on the wire.
        if (this.fetching === url) this.startMusic(buffer);
      } catch (error) {
        console.warn("music unavailable", url, error);
        this.music.set(url, null);
      } finally {
        if (this.fetching === url) this.fetching = null;
      }
    })();
  }

  private startMusic(buffer: AudioBuffer): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    if (this.musicGain === null) {
      this.musicGain = ctx.createGain();
      this.musicGain.gain.value = this.musicLevel;
      // Straight to the destination rather than through `master`: the master
      // is the game's volume, and music has its own.
      this.musicGain.connect(ctx.destination);
    }
    const node = ctx.createBufferSource();
    node.buffer = buffer;
    node.connect(this.musicGain);
    // Up from nothing over two seconds. A track that begins at full volume
    // out of silence is a jump scare with a melody.
    this.musicGain.gain.cancelScheduledValues(ctx.currentTime);
    this.musicGain.gain.setValueAtTime(0, ctx.currentTime);
    this.musicGain.gain.linearRampToValueAtTime(
      this.musicLevel, ctx.currentTime + 2);
    node.start();
    this.musicNode = node;
  }

  /**
   * Freeze everything, keeping its place.
   *
   * Used when the world is paused. Suspending the context rather than
   * stopping the track is what lets a two-minute tune carry on from the bar
   * it was in when somebody pressed escape - a buffer that has been stopped
   * cannot be started again where it was, so cutting it would mean either
   * beginning the track over or losing it, and both are worse than a moment
   * of silence. It also takes the rest of the game's sound with it, which is
   * right: a paused world should not be crackling away behind the menu.
   */
  suspend(): void {
    const ctx = this.ctx;
    if (ctx === null || ctx.state !== "running") return;
    void ctx.suspend();
  }

  /** Cut the music - leaving the map, turning it off, closing the tab. */
  stopMusic(): void {
    this.fetching = null;
    const node = this.musicNode;
    this.musicNode = null;
    if (node === null) return;
    try { node.stop(); } catch { /* already finished */ }
    try { node.disconnect(); } catch { /* already gone */ }
  }

  /**
   * The context's own clock, which stops while it is suspended.
   *
   * Exposed for the checks: "is the sound paused" cannot be answered by a
   * flag somebody set, only by whether time is passing for the audio.
   */
  get clock(): number { return this.ctx?.currentTime ?? 0; }

  /** Whether a track is playing, so a check can say so. */
  get musicPlaying(): boolean { return this.musicNode !== null; }

  /** Whether a track has been fetched. Used by tests. */
  musicReady(url: string): boolean {
    return this.music.get(url) instanceof AudioBuffer;
  }

  /** Whether a take has been fetched, and whether it worked. Used by tests. */
  voiceState(url: string): "unloaded" | "loaded" | "failed" {
    if (!this.voices.has(url)) return "unloaded";
    return this.voices.get(url) === null ? "failed" : "loaded";
  }

  private playBuffer(buffer: AudioBuffer | null, volume: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (ctx === null || master === null || buffer === null) return;
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    source.connect(gain);
    gain.connect(master);
    source.start();
  }

  /** Master level, 0..1. */
  setVolume(value: number): void {
    if (this.master !== null) this.master.gain.value = Math.max(0, Math.min(1, value));
  }
}
